import React, { useState, useEffect } from "react";
import SkeletonLoader from "./SkeletonLoader";
import noPreviewImage from "../../Assets/photo/no-preview-available.webp";

/**
 * LoadingImage Component
 * Displays image with loading skeleton, error handling, and fallback support
 *
 * @param {string} src - Image source URL
 * @param {string} alt - Alternative text
 * @param {string} fallback - Fallback image (default: no-preview-available.webp)
 * @param {number} width - Image width (default: 100%)
 * @param {number} height - Image height (default: auto)
 * @param {string} className - Additional CSS classes
 * @param {object} style - Inline styles
 * @param {function} onLoad - Callback when image loads successfully
 * @param {function} onError - Callback when image fails to load
 * @param {...props} - Additional img element props
 */
const LoadingImage = ({
  src,
  alt = "Image",
  fallback = noPreviewImage,
  width = "100%",
  height = "auto",
  className = "",
  style = {},
  onLoad = () => {},
  onError = () => {},
  skeletonHeight = "200px",
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [imageSrc, setImageSrc] = useState(src);

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    setImageSrc(src);
  }, [src]);

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
    onLoad();
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
    setImageSrc(fallback);
    onError();
  };

  return (
    <div
      style={{
        width,
        height,
        display: "inline-block",
        position: "relative",
        overflow: "hidden",
        ...style,
      }}
      className={className}
    >
      {isLoading && (
        <SkeletonLoader
          width="100%"
          height={skeletonHeight}
          borderRadius={style.borderRadius || "8px"}
        />
      )}
      <img
        src={imageSrc}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: isLoading ? "none" : "block",
          ...style,
        }}
        {...props}
      />
    </div>
  );
};

export default LoadingImage;
